import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, Row, Col, Form, message, Spin, Space } from 'antd';
import { EyeOutlined, SyncOutlined } from '@ant-design/icons';
import { getReportApiByKey } from '@app/api/reports.api';
import ReportFilters from '@app/components/apps/Reports/ReportFilters';

interface ReportFilterBarProps {
  reportKey: string;
  initialFilters: any;
  onFiltersChange: (newFilters: any) => void;
  isEditMode: boolean;
  onEditModeChange: (editMode: boolean) => void;
}

const ReportFilterBar: React.FC<ReportFilterBarProps> = ({
  reportKey,
  initialFilters,
  onFiltersChange,
  isEditMode,
  onEditModeChange,
}) => {
  const [filterOptions, setFilterOptions] = useState<any>({});
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [form] = Form.useForm();

  const storageKey = `report_data`;

  useEffect(() => {
    const loadFilterOptions = async () => {
      if (!reportKey) return;

      setFiltersLoading(true);
      const api = getReportApiByKey(reportKey);
      if (!api) {
        message.error('No API mapping found for this report key.');
        setFiltersLoading(false);
        return;
      }

      try {
        const data = await api.getFilters();
        const transformed = transformApiToFilterOptions(data);
        setFilterOptions(transformed);

        // Load filters from cache if available
        const cached = localStorage.getItem(storageKey);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed?.filters) {
              form.setFieldsValue(parsed.filters);
              return;
            }
          } catch {
            console.warn('Failed to parse cached filters');
          }
        }

        // Set form values from initial filters
        if (Object.keys(initialFilters).length > 0) {
          form.setFieldsValue(initialFilters);
        }
      } catch (err) {
        message.error('Failed to load filter options.');
        console.error(err);
      } finally {
        setFiltersLoading(false);
      }
    };

    loadFilterOptions();
  }, [reportKey, form, initialFilters]);

  const handleCancelEdit = () => {
    // Reset back to last cached filters or initialFilters
    const cached = localStorage.getItem(storageKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed?.filters) {
          form.setFieldsValue(parsed.filters);
        }
      } catch {
        form.setFieldsValue(initialFilters);
      }
    } else {
      form.setFieldsValue(initialFilters);
    }
    onEditModeChange(false);
  };

  const handleRegenerateReport = () => {
    form.validateFields().then((values) => {
      const { dateRange, ...rest } = values;
      const [fromDate, toDate] =
        dateRange?.length === 2
          ? [dateRange[0]?.format('YYYY-MM-DD'), dateRange[1]?.format('YYYY-MM-DD')]
          : [undefined, undefined];

      const modelMap: Record<string, 'LR' | 'NP' | 'PR' | 'IMP/EXP' | 'FR'> = {
        'earnings-by-divisions': 'LR',
        'test-count-by-divisions': 'LR',
        'sample-count-by-divisions': 'LR',
        'item-qty-by-divisions': 'LR',
        'earnings-by-divisions-nom-pay': 'NP',
        'test-count-by-divisions-nom-pay': 'NP',
        'pr-earnings-by-departments': 'PR',
        'pr-count-by-departments': 'PR',
        'registrations-by-establishment': 'PR',
        'imp-exp-earnings-by-departments': 'IMP/EXP',
        'imp-exp-count-by-departments': 'IMP/EXP',
        'imp-exp-earnings-comparison': 'IMP/EXP',
        'imp-exp-counts-comparison': 'IMP/EXP',
        'imp-exp-nominal-earnings': 'IMP/EXP',
        'imp-exp-nominal-count': 'IMP/EXP',
        'imp-exp-levels-by-qty': 'IMP/EXP',
        'consignment-by-country': 'IMP/EXP',
        'imp-exp-tonnage': 'IMP/EXP',
        'h-form': 'FR',
        'summary-report': 'FR',
      };

      const payload = {
        reportKey,
        ...rest,
        ...(fromDate && { from_date: fromDate }),
        ...(toDate && { to_date: toDate }),
        model: modelMap[reportKey] || '',
      };

      onFiltersChange(payload);
      onEditModeChange(false);
      message.success('Report regenerated successfully!');
    });
  };

  const renderFilterValue = (key: string, value: any) => {
    if (value === undefined || value === null) return null;

    if (filtersLoading || Object.keys(filterOptions).length === 0) {
      return <Spin size="small" />;
    }

    const chipStyle: React.CSSProperties = {
      display: 'inline-block',
      padding: '4px 8px',
      margin: '2px 6px 4px 0',
      backgroundColor: '#f0f0f0',
      border: '1px solid #d4d4d4',
      borderRadius: '3px',
      fontSize: '12px',
      color: '#333',
      lineHeight: '1.3',
      whiteSpace: 'nowrap',
      verticalAlign: 'top',
    };

    if (typeof value === 'boolean') {
      return <span style={chipStyle}>{value ? 'Yes' : 'No'}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      return (
        <div
          style={{
            display: 'block',
            lineHeight: '1.8',
          }}
        >
          {value.map((val, index) => (
            <span key={`${val}-${index}`} style={chipStyle}>
              {getFilterDisplayValue(key, val, filterOptions)}
            </span>
          ))}
        </div>
      );
    }

    return <span style={chipStyle}>{getFilterDisplayValue(key, value, filterOptions)}</span>;
  };

  const renderViewMode = () => {
    if (filtersLoading) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Spin size="large" />
          <p style={{ marginTop: 10, color: '#666' }}>Loading filters...</p>
        </div>
      );
    }

    if (Object.keys(filterOptions).length === 0) {
      return <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>Filter options not available</div>;
    }

    const filterLabels = {
      division_ids: 'Division',
      department_ids: 'Department',
      department_types: 'Department Type',
      types: 'Process',
      lab_service_category_ids: 'Service Category/Test',
      lab_service_type: 'Service Type',
      from_date: 'From Date',
      to_date: 'To Date',
      invoice_status: 'Invoice Status',
      date_related_to: 'Count For',
      count_by: 'Count By',
      product_category_ids: 'Category',
      product_category_options: 'Product Category',
      level: 'Level',
      type: 'Consignment Type',
      type_option: 'Process',
      import_types: 'Import Process',
      export_types: 'Export Process',
      year: 'Year',
      with_md: 'With MD',
      with_detail: 'Detailed Report',
      with_partial: 'Partial Payments Included',
      cashier_ids: 'Cashier',
      cashier_location_ids: 'Cashier Location',
    };

    // Exclude reportKey and model from display
    const displayFilters = { ...initialFilters };
    delete displayFilters.reportKey;
    delete displayFilters.model;

    const hasFilters = Object.keys(displayFilters).some(
      (key) => displayFilters[key] && !(Array.isArray(displayFilters[key]) && displayFilters[key].length === 0),
    );

    if (!hasFilters) {
      return <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>No filters applied</div>;
    }

    const renderFilterRow = (keys: string[], colSpan = 12) => {
      const hasContent = keys.some((key) => {
        const value = displayFilters[key];
        return value && !(Array.isArray(value) && value.length === 0);
      });

      if (!hasContent) return null;

      return (
        <Row gutter={[16, 12]} style={{ marginBottom: 16 }}>
          {keys.map((key) => {
            const value = displayFilters[key];
            if (!value || (Array.isArray(value) && value.length === 0)) return null;

            const label = filterLabels[key as keyof typeof filterLabels] || key;

            return (
              <Col span={colSpan} key={key}>
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 6,
                      color: '#555',
                      fontSize: '13px',
                      display: 'inline-block',
                      marginRight: '8px',
                      minWidth: '80px',
                    }}
                  >
                    {label}:
                  </div>
                  <div style={{ display: 'inline-block', verticalAlign: 'top' }}>{renderFilterValue(key, value)}</div>
                </div>
              </Col>
            );
          })}
        </Row>
      );
    };

    console.log('Displaying initial filters:', displayFilters);

    return (
      <div>
        {renderFilterRow(['type', 'type_option', 'level'], 12)}

        {(displayFilters.type || displayFilters.type_option || displayFilters.level) && (
          <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
        )}

        {renderFilterRow(['import_types'], 24)}

        {displayFilters.import_types && (
          <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
        )}

        {renderFilterRow(['export_types'], 24)}

        {displayFilters.export_types && (
          <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
        )}

        {renderFilterRow(['division_ids', 'types'])}

        {(displayFilters.division_ids || displayFilters.types) && displayFilters.lab_service_category_ids && (
          <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
        )}

        {renderFilterRow(['lab_service_category_ids'], 24)}

        {displayFilters.lab_service_category_ids &&
          (displayFilters.lab_service_type || displayFilters.count_by || displayFilters.date_related_to) && (
            <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
          )}

        {renderFilterRow(['lab_service_type', 'count_by', 'date_related_to'], 8)}

        {(displayFilters.lab_service_type || displayFilters.count_by || displayFilters.date_related_to) &&
          (displayFilters.from_date || displayFilters.to_date) && (
            <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
          )}

        {renderFilterRow(['from_date', 'to_date'], 12)}

        {displayFilters.department_ids && (
          <>
            <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
            {renderFilterRow(['department_ids'], 12)}
          </>
        )}

        {displayFilters.department_types && (
          <>
            <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
            {renderFilterRow(['department_types'], 12)}
          </>
        )}

        {displayFilters.invoice_status && (
          <>
            <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
            {renderFilterRow(['invoice_status'], 12)}
          </>
        )}

        {displayFilters.product_category_ids && (
          <>
            <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
            {renderFilterRow(['product_category_ids'], 12)}
          </>
        )}
        {displayFilters.year && (
          <>
            <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
            {renderFilterRow(['year'], 12)}
          </>
        )}
        {displayFilters.product_category_options && (
          <>
            <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
            {renderFilterRow(['Product Category'], 12)}
          </>
        )}
        {displayFilters.product_category_options && (
          <>
            <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
            {renderFilterRow(['Product Category'], 12)}
          </>
        )}
        {(displayFilters.with_md !== undefined || displayFilters.with_detail !== undefined) &&
          displayFilters.with_partial && (
            <>
              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #f0f0f0' }} />
              {renderFilterRow(['with_md', 'with_detail', 'with_partial'], 8)}
            </>
          )}

        {renderFilterRow(['division_ids', 'types'])}

        {(displayFilters.cashier_ids || displayFilters.cashier_location_ids) && (
          <>
            <hr style={{ margin: '8px 0', borderTop: '1px solid #f0f0f0' }} />
            {renderFilterRow(['cashier_ids', 'cashier_location_ids'], 8)}
          </>
        )}
      </div>
    );
  };

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={
        <Space>
          {/* <EyeOutlined /> */}
          Applied Filters
        </Space>
      }
    >
      {isEditMode ? (
        <div>
          {filtersLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
              <p style={{ marginTop: 10 }}>Loading filter options...</p>
            </div>
          ) : (
            <>
              <Form form={form} layout="vertical">
                <div className="hide-in-pdf">
                  <ReportFilters reportKey={reportKey} form={form} filterOptions={filterOptions} />
                </div>
              </Form>
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Space>
                  <Button className="hide-in-pdf" onClick={handleCancelEdit} size="small">
                    Cancel
                  </Button>
                  <Button
                    className="hide-in-pdf"
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={handleRegenerateReport}
                    loading={filtersLoading}
                    size="small"
                  >
                    Regenerate Report
                  </Button>
                </Space>
              </div>
            </>
          )}
        </div>
      ) : (
        renderViewMode()
      )}
    </Card>
  );
};

// Helper function to get display value for filter options
const getFilterDisplayValue = (filterKey: string, value: any, filterOptions: any): string => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  const optionMaps = {
    division_ids: filterOptions.division_options,
    department_ids: filterOptions.departmentOptions,
    types: filterOptions.type_options,
    lab_service_category_ids: filterOptions.lab_service_category_options,
    lab_service_type: filterOptions.lab_service_type_options,
    invoice_status: filterOptions.invoice_status_option,
    date_related_to: filterOptions.date_related_to_option,
    count_by: filterOptions.lab_count_by_options,
    pr_category_id: filterOptions.parent_pr_category_options,
    product_category_ids: filterOptions.product_category_options,
    level: filterOptions.level_options,
    type: filterOptions.type_options,
    type_option: [...(filterOptions.import_types_options || []), ...(filterOptions.export_types_options || [])],
    cashier_ids: filterOptions.cashier_options,
    cashier_location_ids: filterOptions.cashier_location_options,
  };

  const options = optionMaps[filterKey as keyof typeof optionMaps];
  if (options) {
    const option = options.find((opt: any) => opt.value === value);
    return option ? option.label : value;
  }

  return value;
};

// Utility function to transform API data
function transformApiToFilterOptions(apiData: any) {
  return {
    division_options: apiData.division_options || [],
    departmentOptions: apiData.department_options || [],
    type_options: apiData.type_options || [],
    lab_service_type_options: apiData.lab_service_type_options || [],
    lab_service_category_options: apiData.lab_service_category_options || [],
    lab_sample_options: apiData.lab_sample_options || [],
    invoice_status_option: apiData.invoice_status_option || [],
    date_related_to_option: apiData.date_related_to_option || [],
    lab_count_by_options: apiData.lab_count_by_options || [],
    pr_category_options: apiData.pr_category_options || [],
    parent_pr_category_options: apiData.root_pr_category_options || [],
    product_category_options: apiData.product_category_options || [],
    level_options: apiData.level_options || [],
    import_types_options: apiData.import_types_options || [],
    export_types_options: apiData.export_types_options || [],
    cashier_options: apiData.cashier_options || [],
    cashier_location_options: apiData.cashier_location_options || [],
  };
}

export default ReportFilterBar;
